import { DecisionControlLogic, RepeatDataInput, RepeatStep } from '@/types/automationSteps.ts';
import { useUpdateStepFields } from '@/hooks/api/steps/useUpdateStepFields.ts';
import { StepErrorType } from '@/types/stepErrorTypes.ts';
import { Group, Stack } from '@mantine/core';
import { DataInput, InputSource } from '@/types/automationFields.ts';
import StyledTextArea from '@/components/StyledInputFields/StyledTextarea.tsx';
import RepeatConditionMultiDataInputSelector from '@/pages/Editor/components/StepSidebar/StepForms/RepeatStep/RepeatConditionMultiDataInputSelector.tsx';
import StyledNumberInput from '@/components/StyledInputFields/StyledNumberInput.tsx';
import InputLabelWithHoverCard from '@/components/InputLabelWithHoverCard.tsx';
import StyledInputErrorText from '@/components/StyledInputFields/StyledInputErrorText.tsx';
import useStepSpecificError from '@/hooks/useStepSpecificError.ts';
import StyledInputLabel from '@/components/StyledInputFields/StyledInputLabel.tsx';
import { generateUid } from '@/utils/misc.ts';
import RepeatDataInputFields from '@/pages/Editor/components/StepSidebar/StepForms/RepeatStep/RepeatDataInputFields.tsx';
import { DEFAULT_DYNAMIC_DATA_INPUT, STEP_LOGIC_MAX_LENGTH } from '@/utils/constants.ts';
import StepFieldsAddButton from '@/pages/Editor/components/StepFieldsAddButton.tsx';
import InputSourceSelectField from '@/pages/Editor/components/SharedFields/InputSourceSelectField.tsx';
import MultiDataInputSelectorUI from '@/pages/Editor/components/SharedFields/DataInputSelector/MultiDataInputSelector/MultiDataInputSelectorUI.tsx';
import {
  getMultiLogicDataInputs,
  inputSourceAcceptsDataInput,
  inputSourceAcceptsUserSpecified,
} from '@/pages/Editor/components/SharedFields/utils.ts';
import DecisionControlLogicFields from '@/pages/Editor/components/StepSidebar/StepForms/DecisionControlLogicFields.tsx';

type props = {
  step: RepeatStep;
};

type Fields = {
  condition_data_inputs: DataInput[];
  condition_logic_source: InputSource;
  condition_logic_data_inputs: DataInput[];
  condition_description: string;
  control_logic: DecisionControlLogic;
  min_repeat_count: number | 'Any';
  max_repeat_count: number | 'Any';

  repeat_data_inputs: RepeatDataInput[];
};

const RepeatFields = ({ step }: props) => {
  const { fields, onChange, onChangeNoDelay } = useUpdateStepFields<Fields>({ step, syncWithAPI: true });

  const emptyLogicDescription = useStepSpecificError(step, StepErrorType.EmptyDescription);

  const invalidRepeatCount = useStepSpecificError(step, StepErrorType.InvalidRepeatCount);

  const invalidLogicDataInput = useStepSpecificError(step, StepErrorType.InvalidLogicDataInput);
  const invalidProvidedCode = useStepSpecificError(step, StepErrorType.InvalidProvidedCode);

  return (
    <Stack>
      <InputSourceSelectField
        label="Repeat Condition Logic Source"
        value={step.condition_logic_source}
        onChange={(value) => onChangeNoDelay({ condition_logic_source: value })}
      />
      {inputSourceAcceptsUserSpecified(fields.condition_logic_source) && (
        <Stack>
          <StyledTextArea
            label="Repeat Condition Description"
            value={fields.condition_description}
            placeholder="Condition logic description. This will be used to determine when to repeat the step."
            onChange={(event) => {
              onChange({ condition_description: event.currentTarget.value });
            }}
            required={true}
            autosize={true}
            minRows={2}
            error={emptyLogicDescription}
            maxLength={STEP_LOGIC_MAX_LENGTH}
          />
        </Stack>
      )}
      {inputSourceAcceptsDataInput(fields.condition_logic_source) && (
        <MultiDataInputSelectorUI
          stepId={step.step_id}
          label={'Repeat Condition Logic Data Inputs'}
          error={invalidLogicDataInput}
          dataInputs={getMultiLogicDataInputs(fields.condition_logic_data_inputs)}
          updateDataInputs={(dataInputs) =>
            onChangeNoDelay({
              condition_logic_data_inputs: dataInputs,
            })
          }
        />
      )}
      <span>
        <StyledInputLabel>Inputs</StyledInputLabel>

        <Stack>
          {fields.repeat_data_inputs.map((repeatDataInput, idx) => (
            <RepeatDataInputFields
              key={repeatDataInput.output_id}
              condition={repeatDataInput}
              onChangeNoDelay={(updatedInput: RepeatDataInput) => {
                const updatedInputs = fields.repeat_data_inputs.map((input) =>
                  input.output_id === updatedInput.output_id ? updatedInput : input,
                );
                onChangeNoDelay({ repeat_data_inputs: updatedInputs });
              }}
              onChangeDelay={(updatedInput: RepeatDataInput) => {
                const updatedInputs = fields.repeat_data_inputs.map((input) =>
                  input.output_id === updatedInput.output_id ? updatedInput : input,
                );
                onChange({ repeat_data_inputs: updatedInputs });
              }}
              onDelete={() => {
                const updatedInputs = fields.repeat_data_inputs.filter(
                  (input) => input.output_id !== repeatDataInput.output_id,
                );
                onChangeNoDelay({ repeat_data_inputs: updatedInputs });
              }}
              step={step}
              inputIndex={idx}
            />
          ))}

          <StepFieldsAddButton
            stepId={step.step_id}
            elementCount={fields.repeat_data_inputs.length}
            onClick={() => {
              const newInput: RepeatDataInput = {
                output_id: generateUid(),
                name: '',
                data_input: DEFAULT_DYNAMIC_DATA_INPUT,
                child_data_output: DEFAULT_DYNAMIC_DATA_INPUT,
              };
              onChangeNoDelay({ repeat_data_inputs: [...fields.repeat_data_inputs, newInput] });
            }}
          />
        </Stack>
      </span>
      <RepeatConditionMultiDataInputSelector step={step} />
      <DecisionControlLogicFields
        controlLogic={fields.control_logic}
        dataInputs={fields.condition_data_inputs}
        executionTypeLabel="Condition Type"
        invalidProvidedCode={invalidProvidedCode}
        onChange={(control_logic) => onChange({ control_logic })}
        onChangeNoDelay={(control_logic) => onChangeNoDelay({ control_logic })}
      />
      <Stack gap={0}>
        <Group wrap={'nowrap'}>
          <StyledNumberInput
            label={
              <InputLabelWithHoverCard
                label={'Min Repetitions'}
                hoverText={
                  'The minimum number of times to repeat. Leave empty to let system decide'
                }
              />
            }
            placeholder="Any"
            value={fields.min_repeat_count === 'Any' ? '' : fields.min_repeat_count}
            onChange={(newValue) => {
              onChange({ min_repeat_count: typeof newValue === 'string' ? 'Any' : newValue });
            }}
            w={'50%'}
            error={!!invalidRepeatCount}
          />
          <StyledNumberInput
            label={
              <InputLabelWithHoverCard
                label={'Max Repetitions'}
                hoverText={
                  'The maximum number of times to repeat. Leave empty to let system decide'
                }
              />
            }
            placeholder="Any"
            value={fields.max_repeat_count === 'Any' ? '' : fields.max_repeat_count}
            onChange={(newValue) => {
              onChange({ max_repeat_count: typeof newValue === 'string' ? 'Any' : newValue });
            }}
            w={'50%'}
            error={!!invalidRepeatCount}
          />
        </Group>
        <StyledInputErrorText>{invalidRepeatCount}</StyledInputErrorText>
      </Stack>
    </Stack>
  );
};

export default RepeatFields;
